/* 金鑰架構（零知識）。

   主密碼 --Argon2id--> masterKey --HKDF("kek")--> KEK --AES-GCM--> 包住 DEK
     DEK --HKDF("auth")--> authKey --> SHA-256 --> 驗證值（伺服器只用它認裝置）

   為什麼 authKey 由 DEK 派生而不是主密碼：這樣「用救援碼解鎖的裝置」也能通過伺服器驗證
   （它拿不到主密碼，但拿得到 DEK）。主密碼換掉時也不必重新註冊。

   DEK_main 與 DEK_adult 是兩把獨立的隨機金鑰：主密碼外洩也打不開成人區（M4 會再加第二組密碼）。
   救援碼用另一組 Argon2id 參數包住同一組 DEK —— 忘記主密碼時的最後一條路。
   伺服器只拿到「包好的 DEK」與「驗證值」，永遠拿不到主密碼、DEK 或任何明文。 */
import type { Argon2Params } from "./kdf.ts";
import { DEFAULT_ARGON2, deriveRawKey, fromB64, normalizePassphrase, randomBytes, toB64 } from "./kdf.ts";
import { hkdfKey, hkdfSub, importBaseKey, openJson, openWith, sealJson, sha256B64 } from "./box.ts";

export interface WrappedKey { ivB64: string; ctB64: string; kdf: Argon2Params }

export interface KeyRecord {
  version: 1;
  keyId: string;
  createdAt: number;
  /** 伺服器存的驗證值。它只能驗證「你是不是知道主密碼」，不能用來還原任何東西。 */
  authVerifierB64: string;
  wraps: {
    passphrase: WrappedKey;
    recovery?: WrappedKey;
    /** 成人金鑰單獨被主要金鑰包住：讓使用者日後可以替成人區換一組獨立密碼。 */
    adultUnderMain?: { ivB64: string; ctB64: string };
    /** M4：WebAuthn PRF 包裝，只留在本機、不上傳。 */
    prf?: { ivB64: string; ctB64: string; credentialId?: string };
    /** M4：成人區的第二組密碼。 */
    adultPassphrase?: WrappedKey;
  };
}

/** 解鎖後的東西。兩把都是不可匯出的基底金鑰：進到這裡之後就再也拿不回原始位元組。 */
export interface KeyMaterial { keyId: string; main: CryptoKey; adult: CryptoKey }

export class BadPassphraseError extends Error {
  constructor(message = "主密碼不正確，或這份金鑰紀錄已損毀。") {
    super(message);
    this.name = "BadPassphraseError";
  }
}

const KEK_INFO = "lifesage/kek/v1";
const AUTH_INFO = "lifesage/auth/v1";
const ADULT_WRAP_INFO = "lifesage/adult-wrap/v1";

export interface UnlockResult { material: KeyMaterial; authKeyB64: string }

/** 由 DEK 派生伺服器驗證用的金鑰。任何取得 DEK 的方式（主密碼／救援碼／PRF）都會得到同一把。 */
export async function authKeyFromBase(base: CryptoKey): Promise<string> {
  const raw = await hkdfSub(base, AUTH_INFO);
  const out = toB64(raw);
  raw.fill(0);
  return out;
}

export async function verifierFor(authKeyB64: string): Promise<string> {
  return sha256B64(authKeyB64);
}

/** 救援碼用的參數刻意較輕：它有 120 bit 的隨機熵，不需要靠 Argon2 撐住暴力破解。 */
const RECOVERY_ARGON2 = { name: "argon2id", m: 19456, t: 2, p: 1 } as const;

/* ── 救援碼：Crockford base32，去掉容易看錯的 I L O U ── */
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const CODE_LEN = 24;

export function generateRecoveryCode(): string {
  const bytes = randomBytes(CODE_LEN);
  let out = "";
  for (let i = 0; i < CODE_LEN; i++) out += ALPHABET[bytes[i]! % 32];
  return out.match(/.{1,4}/g)!.join("-");
}

export function normalizeRecoveryCode(input: string): string {
  return input.toUpperCase().replace(/[^0-9A-Z]/g, "")
    .replace(/[IL]/g, "1").replace(/O/g, "0").replace(/U/g, "V");
}

export function isWellFormedRecoveryCode(input: string): boolean {
  const code = normalizeRecoveryCode(input);
  return code.length === CODE_LEN && [...code].every((c) => ALPHABET.includes(c));
}

/* ── 內部：把原始金鑰包成 KeyMaterial ── */
async function materialFrom(keyId: string, mainRaw: Uint8Array, adultRaw: Uint8Array): Promise<KeyMaterial> {
  const main = await importBaseKey(mainRaw);
  const adult = await importBaseKey(adultRaw);
  mainRaw.fill(0);
  adultRaw.fill(0);
  return { keyId, main, adult };
}

async function kekFrom(passphrase: string, params: Argon2Params): Promise<CryptoKey> {
  const masterKey = await deriveRawKey(passphrase, params);
  const base = await importBaseKey(masterKey);
  const kek = await hkdfKey(base, KEK_INFO);
  masterKey.fill(0);
  return kek;
}

/** 建立一份新的金鑰紀錄。回傳的 recoveryCode 只會出現這一次。 */
export async function createKeyRecord(
  passphrase: string,
  opts: { keyId?: string; now?: number; params?: Omit<Argon2Params, "saltB64"> } = {},
): Promise<{ record: KeyRecord; material: KeyMaterial; recoveryCode: string; authKeyB64: string }> {
  const now = opts.now ?? Date.now();
  const keyId = opts.keyId ?? "key-" + toB64(randomBytes(9)).replace(/[+/=]/g, "").slice(0, 12);
  const params: Argon2Params = { ...(opts.params ?? DEFAULT_ARGON2), saltB64: toB64(randomBytes(16)) };

  const masterKey = await deriveRawKey(passphrase, params);
  const base = await importBaseKey(masterKey);

  const dekMain = randomBytes(32);
  const dekAdult = randomBytes(32);
  const kek = await hkdfKey(base, KEK_INFO);
  const payload = JSON.stringify({ main: toB64(dekMain), adult: toB64(dekAdult) });
  const passphraseWrap = await sealWrapped(kek, payload, params);

  masterKey.fill(0);
  const mainBase = await importBaseKey(dekMain);
  const authKeyB64 = await authKeyFromBase(mainBase);
  const authVerifierB64 = await verifierFor(authKeyB64);

  const recoveryCode = generateRecoveryCode();
  const recoveryParams: Argon2Params = { ...RECOVERY_ARGON2, saltB64: toB64(randomBytes(16)) };
  const recoveryKek = await kekFrom(normalizeRecoveryCode(recoveryCode), recoveryParams);
  const recoveryWrap = await sealWrapped(recoveryKek, payload, recoveryParams);

  /* 成人金鑰單獨包一份給主要金鑰 —— 使用者日後要替成人區換獨立密碼時，
     需要「在知道主要金鑰的前提下」把成人金鑰拿出來重包，而不是把它變成可匯出。 */
  /* 一定要用「主要 DEK」而不是「主密碼派生出的金鑰」——
     解開時只有主要 DEK 可用（material.main），兩者不一致就永遠打不開。
     這個錯誤是測試抓到的：GCM 認證失敗，而不是靜默產生壞資料。 */
  const adultWrapKey = await hkdfKey(mainBase, ADULT_WRAP_INFO);
  const adultUnderMain = await sealBytes(adultWrapKey, dekAdult);

  const record: KeyRecord = {
    version: 1, keyId, createdAt: now, authVerifierB64,
    wraps: { passphrase: passphraseWrap, recovery: recoveryWrap, adultUnderMain },
  };
  const material = await materialFrom(keyId, dekMain, dekAdult);
  return { record, material, recoveryCode, authKeyB64 };
}

async function sealWrapped(kek: CryptoKey, payload: string, kdf: Argon2Params): Promise<WrappedKey> {
  const { ivB64, ctB64 } = await sealWithString(kek, payload);
  return { ivB64, ctB64, kdf };
}

async function sealWithString(key: CryptoKey, payload: string): Promise<{ ivB64: string; ctB64: string }> {
  const bytes = new TextEncoder().encode(payload);
  const sealed = await sealBytes(key, bytes);
  bytes.fill(0);
  return sealed;
}

async function sealBytes(key: CryptoKey, bytes: Uint8Array): Promise<{ ivB64: string; ctB64: string }> {
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv as unknown as ArrayBuffer }, key, bytes as unknown as ArrayBuffer);
  return { ivB64: toB64(iv), ctB64: toB64(new Uint8Array(ct)) };
}

async function unwrapToMaterial(record: KeyRecord, wrapped: WrappedKey, secret: string): Promise<UnlockResult> {
  let kek: CryptoKey;
  try {
    kek = await kekFrom(secret, wrapped.kdf);
  } catch {
    throw new BadPassphraseError();
  }
  let payload: { main: string; adult: string };
  try {
    const bytes = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromB64(wrapped.ivB64) as unknown as ArrayBuffer },
      kek, fromB64(wrapped.ctB64) as unknown as ArrayBuffer);
    payload = JSON.parse(new TextDecoder().decode(bytes)) as { main: string; adult: string };
  } catch {
    /* GCM 驗證失敗＝密碼不對。不需要另外存驗證值，認證式加密本身就是驗證。 */
    throw new BadPassphraseError();
  }
  const material = await materialFrom(record.keyId, fromB64(payload.main), fromB64(payload.adult));
  return { material, authKeyB64: await authKeyFromBase(material.main) };
}

export async function unlockWithPassphrase(record: KeyRecord, passphrase: string): Promise<UnlockResult> {
  return unwrapToMaterial(record, record.wraps.passphrase, normalizePassphrase(passphrase));
}

export async function unlockWithRecoveryCode(record: KeyRecord, code: string): Promise<UnlockResult> {
  const wrap = record.wraps.recovery;
  if (!wrap) throw new BadPassphraseError("這份紀錄沒有救援碼。");
  if (!isWellFormedRecoveryCode(code)) throw new BadPassphraseError("救援碼格式不對（應為 24 個字元）。");
  return unwrapToMaterial(record, wrap, normalizeRecoveryCode(code));
}

/* ── 成人區的獨立密碼 ──
   成人金鑰本來就包在主要金鑰的包裝裡；這裡額外做一個「用成人密碼包住成人金鑰」的包裝。
   換到什麼：主密碼外洩也打不開成人區。
   代價：多一組密碼要記、多一張救援碼要抄 —— 這個代價在使用者標記第一條成人知識時才付。 */

export async function setAdultPassphrase(
  record: KeyRecord, material: KeyMaterial, adultPassphrase: string,
  opts: { params?: Omit<Argon2Params, "saltB64"> } = {},
): Promise<KeyRecord> {
  const wrap = record.wraps.adultUnderMain;
  if (!wrap) throw new Error("這份金鑰紀錄沒有單獨包裝的成人金鑰，無法設定獨立密碼。");
  const wrappingKey = await hkdfKey(material.main, ADULT_WRAP_INFO);
  const adultRaw = await openWith(wrappingKey, wrap);
  const params: Argon2Params = { ...(opts.params ?? DEFAULT_ARGON2), saltB64: toB64(randomBytes(16)) };
  const kek = await kekFrom(adultPassphrase, params);
  const sealed = await sealBytes(kek, adultRaw);
  adultRaw.fill(0);
  return { ...record, wraps: { ...record.wraps,
    adultPassphrase: { ivB64: sealed.ivB64, ctB64: sealed.ctB64, kdf: params } } };
}

export async function unlockAdultArea(record: KeyRecord, adultPassphrase: string): Promise<CryptoKey> {
  const wrap = record.wraps.adultPassphrase;
  if (!wrap) throw new BadPassphraseError("這個同步空間還沒有設定成人區的獨立密碼。");
  const kek = await kekFrom(adultPassphrase, wrap.kdf);
  try {
    const raw = await openWith(kek, wrap);
    const key = await importBaseKey(raw);
    raw.fill(0);
    return key;
  } catch {
    throw new BadPassphraseError("成人區密碼不正確。");
  }
}

/** 有沒有設定獨立密碼？介面要據此決定顯示「輸入密碼」還是「設定密碼」。 */
export function hasAdultPassphrase(record: KeyRecord | null): boolean {
  return !!record?.wraps.adultPassphrase;
}

/** 把密封的內容用筆記本層級的金鑰打開（同步用）。 */
export { openJson, sealJson };
